import {
    Component,
    OnInit,
    Input,
    ViewChild,
    ElementRef,
    AfterViewChecked,
    OnDestroy,
} from '@angular/core';
import { Observable } from 'rxjs';
import { filter, first, take, tap } from 'rxjs/operators';
import { ChatMessageComponent } from './chat-message/chat-message.component';
import { ChatControlsComponent } from './chat-controls/chat-controls.component';
import { TypingIndicatorComponent } from './typing-indicator/typing-indicator.component';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { ChatHeaderComponent } from './chat-header/chat-header.component';
import {
    Chat,
    ChatItem,
    ChatItemMessage,
    Thread,
} from 'src/app/models/ai-chat-models/chat.model';
import { UserService } from 'src/app/services/user.service';
import { ProfileImage } from 'src/app/interfaces/profile-image';
import { AiChatService } from 'src/app/services/ai-chat-service';
import { MatIconModule } from '@angular/material/icon';
import {
    MatExpansionModule,
    MatExpansionPanel,
    MatExpansionPanelDescription,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { AppStateService } from 'src/app/services/app-state-service';
import { Me } from 'src/app/models/me.model';
import { USER_ROLE } from 'src/app/constants/settingContants';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CalendarCommonModule } from 'angular-calendar';
import { ThreadListComponent } from './thread-list/thread-list.component';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';

@Component({
    selector: 'app-chat',
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.scss'],
    imports: [
        ThreadListComponent,
        ChatMessageComponent,
        ChatControlsComponent,
        TypingIndicatorComponent,
        NgFor,
        ChatHeaderComponent,
        NgIf,
        AsyncPipe,
        MatIconModule,
        MatExpansionPanel,
        MatExpansionModule,
        MatExpansionPanelHeader,
        MatExpansionPanelDescription,
        MatExpansionPanelTitle,
        MatProgressSpinnerModule,
        MatCheckboxModule,
        CalendarCommonModule,
        NgClass,
        MatIconModule,
        MatButtonModule,
        MatToolbarModule,
    ],
    standalone: true,
})
export class ChatComponent implements OnInit, AfterViewChecked, OnDestroy {
    @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
    @Input() height: string;
    @Input() width: string;
    @Input() openDialogEvent: boolean;
    @ViewChild('panel') myPanel: MatExpansionPanel;

    private _chatHasInitialized = false;
    protected panelOpenState = false;
    protected chat$: Observable<Chat>;
    protected threads$: Observable<Thread[]>;
    protected aiBuffering$: Observable<boolean>;
    protected threadBuffering$: Observable<boolean>;
    protected newThreadBeingSaved$: Observable<boolean>;
    protected chatLoading$: Observable<boolean>;
    protected localUser: { userData: Me; photo: string } = {
        userData: {} as Me,
        photo: '',
    };
    protected newMessage: boolean = false;
    protected messages: ChatItemMessage[] = [];
    protected chat: Chat;
    protected threads: Thread[];
    protected threadMenuOpen: boolean = false;

    constructor(
        private userService: UserService,
        private aiChatService: AiChatService,
        private appStateService: AppStateService,
    ) {}

    ngOnInit(): void {
        this.appStateService.me$.subscribe((me: Me) => {
            if (me && (me?.id ?? '').length > 0) {
                this.localUser.userData = me;
                this.userService
                    .get(['profile-image'])
                    .subscribe((profileImage: ProfileImage) => {
                        this.localUser.photo = profileImage.base64Value;
                    });
            }
        });

        this.chat$ = this.aiChatService.chat.pipe(
            tap((chat: Chat) => this.integrateNewMessages(chat)),
        );

        this.chat$
            .pipe(
                filter((chat: Chat) => chat !== null),
                take(1),
            )
            .subscribe(() => (this._chatHasInitialized = true));

        this.threads$ = this.aiChatService.threads.pipe(
            tap((threads: Thread[]) => this.populateThreadList(threads)),
        );

        this.aiBuffering$ = this.aiChatService.aiBuffering;
        this.threadBuffering$ = this.aiChatService.threadBuffering;
        this.newThreadBeingSaved$ = this.aiChatService.newThreadSaving;
        this.chatLoading$ = this.aiChatService.chatLoading;
    }

    ngAfterViewChecked(): void {
        if (this.newMessage) {
            this.newMessage = false;
            setTimeout(() => this.scrollBottom());
        }
    }

    ngOnDestroy(): void {
        if (this.chat != null) {
            this.aiChatService.manageThreads();
        }
    }

    get chatHasInitialized(): boolean {
        return this._chatHasInitialized;
    }

    protected clearChat(): void {
        this.chat = null;
        this.messages = [];
    }

    protected createInitialThread(): void {
        if (this.chat != null) {
            return;
        }

        this.loadNewMessageWindow();
    }

    protected createNewThread(): void {
        this.clearChat();
        this.loadNewMessageWindow();
    }

    private loadNewMessageWindow(): void {
        this.aiChatService.sendWelcomeMessage();
    }

    protected sendMessage(message: string): void {
        this.aiChatService.sendMessage(message);
    }

    private populateThreadList(threads: Thread[]): void {
        this.threads = threads;
    }

    protected loadSelectedThread(thread: Thread): void {
        this.clearChat();
        this.aiChatService.getChatByThreadId(thread.threadId);
    }

    protected deleteThreads(threads: Thread[]): void {
        const threadIds = threads.map((thread: Thread) => thread.threadId);
        if (
            threadIds.some(
                (threadId: string) => threadId === this.chat.threadId,
            )
        ) {
            // If they delete the chat they are currently in, chat will emit null, so we want to clear the client side rendered chat and unsubscribe. If this errors, it will emit the current chat, so this subscription will always complete.
            this.chat$.pipe(first()).subscribe((chat: Chat) => {
                if (chat === null) {
                    this.clearChat();
                }
            });
        }

        this.aiChatService.deleteThreadsById(threads.map((t) => t.threadId));
    }

    private integrateNewMessages(chat: Chat): void {
        this.chat = chat;
        // Behavior subject is initially null
        if (chat === null) {
            return;
        }

        this.newMessage = true;
        const temporaryMessage: ChatItemMessage = chat.completeChat.find(
            (chatItem: ChatItem) => chatItem.message.id.startsWith('temp-'),
        )?.message;

        if (
            temporaryMessage != undefined &&
            !this.messages.find(
                (message: ChatItemMessage) =>
                    message.id.startsWith('temp-') &&
                    message.role === USER_ROLE,
            )
        ) {
            this.messages.push(temporaryMessage);
            return;
        } else {
            const tempMessageExists = this.messages.find(
                (message: ChatItemMessage) =>
                    message.id.startsWith('temp-') &&
                    message.role === USER_ROLE,
            );

            if (tempMessageExists) {
                this.messages.pop();
            }

            if (this.messages.length + 1 > chat.completeChat.length) {
                this.messages = [];
            }

            chat.completeChat.forEach((chatItem: ChatItem) => {
                if (
                    !this.messages.some(
                        (message: ChatItemMessage) =>
                            message.id === chatItem.message.id,
                    )
                ) {
                    this.messages.push(chatItem.message); // add brand new message
                }
            });
        }
    }

    protected trackByCreated(index: number, message: ChatItemMessage): string {
        return message.createdAt;
    }

    private scrollBottom(): void {
        try {
            this.scrollContainer.nativeElement.scrollTop =
                this.scrollContainer.nativeElement.scrollHeight;
        } catch (err) {}
    }

    protected setPanelState(panelState: boolean): void {
        if (panelState) {
            this.chat = null;
            this._chatHasInitialized = false;
        } else {
            this.myPanel.close();
        }
    }
}
